import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { useRef, useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentApi } from '@/modules/document/hooks/use-document-api';
import { useDocumentsListInit } from '@/modules/document/hooks/use-documents-list-init';
import { useFilteredDocumentsListInit } from '@/modules/document/hooks/use-filtered-document-list-init';
import { useUploadFileByChunks } from '@/modules/project/hooks/use-upload-file-by-chunks';
import { DOCUMENT_TYPE_EXTENSION } from '@/modules/document/document.const';

export const useFileUpload = () => {
	const t = useTranslations();
	const { project, searchStatusId, showToast } = useAppContext();
	const [file, setFile] = useState<File | undefined>(undefined);
	const uploadFileByChunks = useUploadFileByChunks();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { uploadProjectDocumentFile, isPending } = useDocumentApi();

	const convertBytesToMB = (bytes: number) => (bytes / 1024 ** 2).toFixed(2);

	const { refetch: refetchProjectDocuments } = useDocumentsListInit({
		projectId: project?.documentId,
		statusId: searchStatusId,
		isProjectOpen: !!project?.documentId,
	});
	const { refetch: refetchProjectHistory } = useFilteredDocumentsListInit({
		projectId: project?.documentId,
		date: null,
		documentType: null,
		projectDocumentId: undefined,
	});

	const validateFile = (file: File, title: Data.Entity<'api::document-type.document-type'>['title']) => {
		if (!file) return false;

		const splitName = file.name.split('.');
		const type = splitName[splitName.length - 1];

		if (!title) return false;

		const isValid = DOCUMENT_TYPE_EXTENSION[title as keyof typeof DOCUMENT_TYPE_EXTENSION] === type;

		if (!isValid) {
			showToast({ message: t('T_FILE_NOT_VALID'), variant: 'error' });
		}
		return isValid;
	};

	const validateAndSetFile = (file: File, title: Data.Entity<'api::document-type.document-type'>['title']) => {
		if (!file) return;

		const isFileValid = validateFile(file, title);

		if (file && isFileValid) {
			setFile(file);
		}
	};

	const handleDrop = (e: React.DragEvent, title: Data.Entity<'api::document-type.document-type'>['title']) => {
		e.preventDefault();
		validateAndSetFile(e.dataTransfer.files[0], title);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, title: Data.Entity<'api::document-type.document-type'>['title']) => {
		if (!e.target.files) return;

		validateAndSetFile(e.target.files[0], title);
	};

	const handleReset = () => {
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const onClear = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
		e.preventDefault();
		setFile(undefined);
		handleReset();
	};

	const onSuccessFileUpload = () => {
		refetchProjectDocuments();
		refetchProjectHistory();
	};

	const handleSubmit = async ({
		documentTypeName,
		region,
	}: {
		documentTypeName: string;
		region?: Data.Entity<'api::project-document.project-document'>['region'];
	}) => {
		if (!file) return;

		await uploadFileByChunks({
			file,
			successCallback: onSuccessFileUpload,
			onReset: handleReset,
			documentTypeName,
			uploadFunction: uploadProjectDocumentFile,
			...(documentTypeName === 'T_CURVES' && { region }),
		});
	};

	return {
		file,
		fileInputRef,
		isPending,
		setFile,
		validateFile,
		convertBytesToMB,
		handleDrop,
		handleFileSelect,
		handleSubmit,
		onClear,
	};
};
